import { loadFromLocalStorage } from 'utils/localStorageHelpers';

// Google Analytics 4.
//
// The site was tagged with Universal Analytics (UA-123510303-1) until this module
// replaced it. Google stopped processing UA data on 1 July 2023, so those tags had been
// collecting nothing for years.
//
// The measurement id carries a value only in .env.production; everywhere else it is
// declared empty, which turns every function here into a no-op and keeps developer and QA
// traffic out of the property instead of relying on filters after the fact. Those empty
// declarations are load-bearing, not tidiness: react-scripts forces NODE_ENV=production
// before reading env files, so .env.production is layered onto every build, and a key left
// undeclared elsewhere is inherited from it. See the note in .env.staging.
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

export const isAnalyticsEnabled = () => Boolean(MEASUREMENT_ID);

// gtag.js reads its queue off window.dataLayer and expects the raw `arguments` object;
// pushing a rest-parameter array instead makes the tag ignore the call. That is why this
// is a function declaration and not an arrow.
function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

let started = false;

export const initAnalytics = () => {
  if (!isAnalyticsEnabled() || started) return;

  started = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  gtag('js', new Date());
  // send_page_view is off because this is a single-page app: the automatic view fires
  // once on boot and never again, so every later navigation would go unrecorded.
  // trackPageView() sends all of them, the first one included, so each counts once.
  gtag('config', MEASUREMENT_ID, { send_page_view: false });
};

export const trackPageView = (path) => {
  if (!isAnalyticsEnabled()) return;

  // The location is the only dimension worth setting. There is no per-route title to send:
  // index.html ships one marketing title, screens/Landing is the only screen that changes
  // it, and react-helmet 6 commits the change inside a requestAnimationFrame — after this
  // runs, wherever it is called from. Note that omitting it does not mean GA4 records no
  // title: gtag attaches document.title itself, so every authenticated screen is filed
  // under the marketing title. Page path is the dimension to report on until the screens
  // carry titles of their own.
  gtag('event', 'page_view', {
    page_location: `${window.location.origin}${path}`,
  });
};

// GA4's User-ID feature, sent as the internal client id — the same id the Rails client
// layout reported to Universal Analytics, though it is new to this app. It is an account
// key rather than a name or an email, and it lets one client be recognised as one person
// across devices. null clears it on sign-out; otherwise the next person on a shared
// machine is attributed to whoever signed out last.
export const setAnalyticsUserId = (userId) => {
  if (!isAnalyticsEnabled()) return;

  gtag('set', { user_id: userId == null ? null : String(userId) });
};

const currentUserId = (store) => store.getState().user?.currentUser?.id ?? null;

// Written by the sign-in thunk and cleared on sign-out, so it outlives a page reload where
// the redux slice does not.
const storedUserId = () => {
  // Reading storage can throw — Safari blocks it outright in private mode, and a corrupt
  // entry fails to parse. This runs at boot, before render, so an escaping error would
  // blank the whole app including the public landing page, which otherwise never touches
  // storage. Analytics is never worth taking the site down for.
  try {
    // Gated on the session token, not on currentUser. Every exit path clears authHeaders,
    // but the 2FA exit button leaves currentUser behind — without this gate that stale id
    // would be reported for anonymous browsing afterwards.
    if (!loadFromLocalStorage('authHeaders')) return null;

    return loadFromLocalStorage('currentUser')?.id ?? null;
  } catch (error) {
    return null;
  }
};

// Sign-in and sign-out are plain redux state changes, so the id is picked up by watching
// the store rather than by reaching into the auth thunks. The store is passed in to keep
// this module free of a redux import, which also makes it testable with a stub.
//
// Call this before the entry page view. The slice starts empty on every reload —
// currentUser is only restored once DefaultLayout's fetchUser resolves — so the id is
// seeded from localStorage, where sign-in already stored the client. Without that seed a
// returning client's first page view of each visit would be anonymous. One gap remains:
// the sign-in thunk navigates before its fulfilled action reaches the store, so the single
// page view landing on /makers is still unattributed.
export const trackUserId = (store) => {
  if (!isAnalyticsEnabled()) return;

  let previous = currentUserId(store) ?? storedUserId();
  if (previous !== null) setAnalyticsUserId(previous);

  store.subscribe(() => {
    // Falls back to storage for the same reason the seed does: the first dispatch after
    // boot — ConnectedRouter's LOCATION_CHANGE, or fetchUser.pending — arrives while the
    // slice is still empty, and reading redux alone would clear the seeded id right after
    // it was set. On sign-out both are cleared together, so this still reports null.
    const current = currentUserId(store) ?? storedUserId();
    if (current === previous) return;

    previous = current;
    setAnalyticsUserId(current);
  });
};
